import { Link as RouterLink } from "react-router";

import { Link as MuiLink } from "@mui/material";
import { formatInsuranceNumber, useClientStatusLabel } from "@shared";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import { ClientSearchResult, clientsApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { createColumns } from "@/common/components/table/DataTable.utils";
import { useClientsSearchContext } from "@/common/contexts/clients-search-context/ClientsSearchContext";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { QueryKey } from "@/query/Query";
import { clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { ClientsTableWidth } from "../Clients.utils";

export function useClients() {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const { searchParams, filters, handleFilterUpdate, removeAllFilters } = useClientsSearchContext();
  const statusLabelOf = useClientStatusLabel();

  const { data, isLoading, isError } = useQuery({
    queryKey: [QueryKey.Clients, searchParams.toString()],
    queryFn: () =>
      clientsApi.searchClients(
        {
          searchText: filters?.searchText ?? undefined,
          extendedSearchSalutation: filters?.salutation ?? undefined,
          extendedSearchTitle: filters?.title ?? undefined,
          extendedSearchName: filters?.name ?? undefined,
          extendedSearchBirthday: filters?.birthday ?? undefined,
          extendedSearchInsuranceNumber: filters?.insuranceNumber ?? undefined,
          extendedSearchCaseNumber: filters?.caseNumber ?? undefined,
          extendedSearchSrvFileNumber: filters?.srvFileNumber ?? undefined,
          extendedSearchCourtId: filters?.courtId ?? undefined,
          extendedSearchStreet: filters?.street ?? undefined,
          extendedSearchZipCode: filters?.zipCode ?? undefined,
          extendedSearchCity: filters?.city ?? undefined,
          extendedSearchNationality: filters?.nationality ?? undefined,
          extendedSearchMediaInquiry: filters?.mediaInquiry ?? undefined,
          extendedSearchCaseNoteDateFrom: filters?.caseNoteDateFrom ?? undefined,
          extendedSearchCaseNoteDateTo: filters?.caseNoteDateTo ?? undefined,
          extendedSearchCaseNoteKeyword: filters?.caseNoteKeyword ?? undefined,
          extendedSearchAssignedCounselorId: filters?.assignedCounselorId ?? undefined,
          extendedSearchStatus: filters?.status ?? undefined,
        },
        withLocalErrorHandling
      ),
    meta: { loadingScope: "pageContent" },
    staleTime: Infinity,
    // The failure is shown in place of the result list, so it has to arrive as soon as it is known:
    // retrying would leave the SUL-Mitarbeiter:in on a loading table for seconds of backoff before
    // the search-unavailable message appears, and they can simply search again.
    retry: false,
  });

  const columnConfigurations = createColumns<ClientSearchResult>([
    {
      field: "caseNumber",
      headerName: t("clients.table.id"),
      sortable: true,
      flex: 1,
      maxWidth: ClientsTableWidth.caseNumber,
      renderCell: (params) => (
        <MuiLink
          component={RouterLink}
          to={clientPath.byId({ id: (params.row as ClientSearchResult).clientId })}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          tabIndex={params.hasFocus ? 0 : -1}
          underline="none"
          color="inherit"
          aria-label={t("clients.openClient", {
            name: (params.row as ClientSearchResult).firstName + " " + (params.row as ClientSearchResult).lastName,
          })}
        >
          <EllipsisText value={params.value as string} variant={"body2"} />,
        </MuiLink>
      ),
    },
    {
      field: "status",
      headerName: t("clients.table.status"),
      sortable: true,
      flex: 1,
      maxWidth: ClientsTableWidth.status,
      renderCell: (params) => <EllipsisText value={statusLabelOf(params.value as string | null)} variant={"body2"} />,
    },
    {
      field: "firstName",
      headerName: t("clients.table.firstName"),
      sortable: true,
      flex: 1,
      renderCell: (params) => <EllipsisText value={params.value as string} variant={"body2"} />,
    },
    {
      field: "lastName",
      headerName: t("clients.table.lastName"),
      sortable: true,
      flex: 1,
      renderCell: (params) => <EllipsisText value={params.value as string} variant={"body2"} />,
    },
    {
      field: "assignedCounselor",
      headerName: t("common.counselor"),
      sortable: true,
      flex: 1,
      maxWidth: ClientsTableWidth.counselor,
      renderCell: (params) => <EllipsisText value={params.value as string} variant={"body2"} />,
    },
    {
      field: "birthday",
      headerName: t("clients.table.birthday"),
      sortable: false,
      flex: 1,
      maxWidth: ClientsTableWidth.birthday,
      renderCell: (params) =>
        params.value !== null ? format(params.value as Date, "P", { locale: locales[language] }) : null,
    },
    {
      field: "insuranceNumber",
      headerName: t("clients.table.socialSecurityNumber"),
      sortable: false,
      flex: 1,
      maxWidth: ClientsTableWidth.insuranceNumber,
      renderCell: (params) => formatInsuranceNumber(params.value as string | null),
    },
    {
      field: "phone",
      headerName: t("clients.table.telephoneNumber"),
      sortable: false,
      flex: 1,
      maxWidth: ClientsTableWidth.phone,
      renderCell: (params) => <EllipsisText value={params.value as string} variant={"body2"} />,
    },
    {
      field: "clientId",
      headerName: t("clients.table.personalBankruptcyFileNumber"),
      sortable: false,
      flex: 1,
      maxWidth: ClientsTableWidth.personalBankruptcyFileNumber,
      renderCell: (params) => <EllipsisText value={params.value as string} variant={"body2"} />,
    },
  ]);

  return {
    clients: data?.data ?? [],
    hasAdditionalClients: data?.hasMoreItems,
    isLoading,
    isError,
    columnConfigurations,
    // The search params already identify the result set, so they double as the signature that sends
    // the table back to page 1 whenever the search or a filter changes.
    paginationResetKey: searchParams.toString(),
    filters,
    onFiltersUpdate: handleFilterUpdate,
    removeAllFilters,
  };
}
